import { Component, Inject, OnInit, Optional } from '@angular/core';
import { ModalService } from '@app/core/services/modal.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { SrRefundRow, SrRefundService, SrTravelBillGet } from '@app/services/sr-refund.service';

@Component({ selector: 'tt-sr-travel-bill', templateUrl: './sr-travel-bill.component.html', styleUrls: ['./sr-travel-bill.component.css'], standalone: false })
export class SrTravelBillComponent implements OnInit {
    public page?: SrTravelBillGet;
    public summary: SrRefundRow[] = [];
    public approving = false;
    public paying = false;
    public updating = false;
    public error = '';

    constructor(private readonly refund: SrRefundService, private readonly modal: ModalService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator) {
        this.navigator = useNavigatorFor(navigator, this);
    }
    public async ngOnInit(): Promise<void> {
        try {
            this.page = await this.refund.getTravelBill({ travelbill_no: String(this.navigator?.params['travelbill_no'] ?? '') });
            await this.loadSummary();
        } catch (error) { this.showError(error); }
    }
    private async loadSummary(): Promise<void> {
        if (!this.page?.travelbill_no?.trim() || !this.page.view_id?.trim()) return;
        this.summary = [];
        this.summary = await this.refund.getTravelBillSummary({ travelbill_no: this.page.travelbill_no, view_id: this.page.view_id });
    }
    public async approve(): Promise<void> { await this.complete('approve'); }
    public async pay(): Promise<void> { await this.complete('pay'); }
    private async complete(action: 'approve' | 'pay'): Promise<void> {
        const busy = action === 'approve' ? 'approving' : 'paying';
        if (!this.page || this[busy]) return;
        this[busy] = true;
        try {
            const params = { travelbill_no: this.page.travelbill_no };
            const response = await (action === 'approve' ? this.refund.approveTravelBill(params) : this.refund.payTravelBill(params));
            if (response.errorcode !== '0') await this.showWarning(response.errormessage);
            else {
                if (action === 'pay') this.paying = false;
                this.navigator?.back();
            }
        } catch (error) { this.showError(error); }
        finally { this[busy] = false; }
    }
    public async update(): Promise<void> {
        if (!this.page || this.updating) return;
        this.updating = true;
        try {
            const response = await this.refund.updateTravelBill({ travelbill_no: this.page.travelbill_no });
            if (response.errorcode !== '0') await this.showWarning(response.errormessage);
            // The legacy flow also reloads the summary after the warning is dismissed.
            this.updating = false;
            await this.loadSummary();
        } catch (error) { this.showError(error); }
        finally { this.updating = false; }
    }
    public openItem({ item, event }: { item: SrRefundRow; event: MouseEvent }): void {
        if (!item.item_state) return;
        const to = ['srtravelbill', 'srtravelbillspec', 'srreportmenu', 'srrefundbasis', 'srrefundinputcrew'].includes(item.item_state) ? `${item.item_state}-test` : item.item_state;
        const target = { to, parms: item.item_parms };
        if (event.ctrlKey) this.navigator?.newTab(target); else this.navigator?.go(target);
    }
    private showWarning(message: string): Promise<void> {
        return this.modal.show({ type: 'warning', title: 'Varsel', message, buttons: [{ label: 'OK', cssClass: 'btn-warning', close: true }] });
    }
    private showError(error: unknown): void { this.error = error instanceof Error ? error.message : String(error); }
}
